import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { FilePlus, Search, BarChart3, LogOut, TrendingUp, Menu, X, Building2, Users, KeyRound } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import NewLogEntry from "@/pages/NewLogEntry";
import OfflineLogs from "@/pages/OfflineLogs";
import OfflineAnalytics from "@/pages/OfflineAnalytics";
import AdvancedAnalytics from "@/pages/AdvancedAnalytics";
import ManageOrganizations from "@/pages/ManageOrganizations";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import SetPassword from "@/pages/SetPassword";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "";
export const API = `${BACKEND_URL}/api`;

const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, setIsOpen]);

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40 lg:hidden" 
          onClick={() => setIsOpen(false)} 
        />
      )}

      <aside className={`fixed left-0 top-0 h-full w-72 bg-white border-r border-slate-100 z-50 flex flex-col shadow-[4px_0_24px_rgba(0,0,0,0.02)] transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button 
          className="absolute top-3 right-3 p-1 rounded-lg hover:bg-slate-100 lg:hidden"
          onClick={() => setIsOpen(false)}
        >
          <X className="w-5 h-5 text-slate-500" />
        </button>

        <div className="px-4 pt-3 pb-2 border-b border-slate-100">
          <div className="flex flex-col items-center">
            <img 
              src="/dolphin-ooh-logo.png"
              alt="Dolphin Retail Media"
              className="h-14 object-contain"
            />
            <h1 
              className="text-[9px] font-bold tracking-[0.15em] text-[#006994] mt-1"
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            >
              DOLPHIN VENUE VISION
            </h1>
          </div>
        </div>

        {user && (
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-10 h-10 rounded-full border-2 border-slate-100"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#006994] flex items-center justify-center text-white font-medium">
                  {user.name?.charAt(0) || user.email?.charAt(0) || "U"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{user.name}</p>
                <p className="text-xs text-slate-500 truncate">{user.email}</p>
                {user.user_type === "org" && user.org_name && (
                  <p className="text-[10px] text-[#006994] font-medium truncate">{user.org_name}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 px-4 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 px-3 mb-3">Menu</p>
          <div className="space-y-1">
            <NavLink
              to="/analytics"
              data-testid="nav-analytics"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-[#006994] text-white shadow-md shadow-[#006994]/20'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              <BarChart3 className="w-5 h-5" strokeWidth={1.5} />
              Offline Analytics
            </NavLink>

            <NavLink
              to="/advanced-analytics"
              data-testid="nav-advanced-analytics"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-[#006994] text-white shadow-md shadow-[#006994]/20'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              <TrendingUp className="w-5 h-5" strokeWidth={1.5} />
              Advanced Analytics
            </NavLink>

            <NavLink
              to="/logs"
              data-testid="nav-offline-logs"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-[#006994] text-white shadow-md shadow-[#006994]/20'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              <Search className="w-5 h-5" strokeWidth={1.5} />
              Offline Logs
            </NavLink>

            {/* Internal users only */}
            {(!user || user.user_type === "internal") && (
              <NavLink
                to="/new-log"
                data-testid="nav-new-log"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-[#006994] text-white shadow-md shadow-[#006994]/20'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`
                }
              >
                <FilePlus className="w-5 h-5" strokeWidth={1.5} />
                New Log Entry
              </NavLink>
            )}

            {/* Internal users only - Manage Organizations */}
            {user && user.user_type === "internal" && (
              <NavLink
                to="/manage-orgs"
                data-testid="nav-manage-orgs"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-[#006994] text-white shadow-md shadow-[#006994]/20'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`
                }
              >
                <Building2 className="w-5 h-5" strokeWidth={1.5} />
                Organizations
              </NavLink>
            )}

            {/* Org Admin - Manage Users */}
            {user && user.user_type === "org" && user.org_role === "admin" && (
              <NavLink
                to="/manage-orgs"
                data-testid="nav-manage-users"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-[#006994] text-white shadow-md shadow-[#006994]/20'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`
                }
              >
                <Users className="w-5 h-5" strokeWidth={1.5} />
                Manage Users
              </NavLink>
            )}
          </div>
        </nav>

        <div className="px-4 py-3 border-t border-slate-100">
          {user?.user_type === "org" && (
            <NavLink
              to="/set-password"
              data-testid="nav-change-password"
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-xl transition-all duration-200"
            >
              <KeyRound className="w-5 h-5" strokeWidth={1.5} />
              Change Password
            </NavLink>
          )}
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-200"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            Sign Out
          </button>
        </div>

        <div className="px-5 py-4 border-t border-slate-100">
          <p className="text-xs text-slate-400 text-center">Dolphin Digital OOH Media &copy; 2026</p>
        </div>
      </aside>
    </>
  );
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-100 z-30 flex items-center px-4 lg:hidden">
        <button 
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-slate-100"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5 text-slate-700" />
        </button>
        <img 
          src="/dolphin-ooh-logo.png" 
          alt="Dolphin OOH" 
          className="h-8 object-contain ml-3" 
        />
      </header>

      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <main className="lg:ml-72 min-h-screen pt-14 lg:pt-0">
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/set-password"
              element={
                <ProtectedRoute allow={(u) => u.user_type === "org"}>
                  <SetPassword />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/analytics" replace />} />

            <Route 
              path="/analytics" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <OfflineAnalytics />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/advanced-analytics" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdvancedAnalytics />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/logs" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <OfflineLogs />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/new-log" 
              element={
                <ProtectedRoute allow={(u) => u.user_type === "internal"}>
                  <Layout>
                    <NewLogEntry />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/manage-orgs" 
              element={
                <ProtectedRoute allow={(u) => u.user_type === "internal" || u.org_role === "admin"}>
                  <Layout>
                    <ManageOrganizations />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<Navigate to="/analytics" replace />} />
          </Routes>

          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;